import React, { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import axiosInstance from '../../components/util/AxiosUtil';
import { getAllUnansweredSeqs, NUMBER_ONLY_SEQS } from './SurveyRoles';
import SurveyMdlModal from './SurveyMdlModal';
import {
    Box, Button, Typography, TextField,
    FormGroup, FormControlLabel, Checkbox,
    Radio, RadioGroup, FormControl,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

function ScrollHintOverlay({ onDismiss }) {
    const onDismissRef = useRef(onDismiss);
    useEffect(() => {
        const timer = setTimeout(() => onDismissRef.current(), 2500);
        return () => clearTimeout(timer);
    }, []);

    return (
        <Box
            onClick={onDismiss}
            sx={{
                position: 'fixed', inset: 0, zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.55)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <KeyboardArrowUpIcon sx={{ fontSize: 48, color: '#fff', animation: 'bounceUp 1.2s ease-in-out infinite' }} />
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: 1 }}>
                    위아래로 스크롤하여 진행하세요
                </Typography>
                <KeyboardArrowDownIcon sx={{ fontSize: 48, color: '#fff', animation: 'bounceDown 1.2s ease-in-out infinite' }} />
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, mt: 3 }}>
                화면을 터치하면 닫힙니다
            </Typography>
            <style>{`
                @keyframes bounceUp {
                    0%, 100% { transform: translateY(0); opacity: 0.5; }
                    50% { transform: translateY(-10px); opacity: 1; }
                }
                @keyframes bounceDown {
                    0%, 100% { transform: translateY(0); opacity: 0.5; }
                    50% { transform: translateY(10px); opacity: 1; }
                }
            `}</style>
        </Box>
    );
}

const SECTION_TITLES = {
    1:  '제1절 업체 기본현황',
    6:  '제2절 RTU 기술 사양 및 통신 방식',
    11: '제3절 펌웨어 및 원격 업데이트(FOTA) 현황',
    15: '제4절 설비 연동 및 인터페이스 현황',
    17: '제5절 운영 실태 및 향후 계획',
    24: '제6절 업계 현장 의견 (자유서술)',
};

const defaultValues = {
    compNm: '',
    managerNm: '',
    phoneNo: '',
    email: '',
};

function SurveyPage() {

    const [questions, setQuestions] = useState([]);
    const [mdlQuestions, setMdlQuestions] = useState([]);
    const [answerMap, setAnswerMap] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [answers, setAnswers] = useState({});
    const [extraTexts, setExtraTexts] = useState({});
    const [unansweredSet, setUnansweredSet] = useState(new Set());
    const [showHint, setShowHint] = useState(true);
    const dismissHint = useCallback(() => setShowHint(false), []);
    const [equipmentList, setEquipmentList] = useState([]);
    const [mdlModalOpen, setMdlModalOpen] = useState(false);

    const { register, getValues, trigger, formState: { errors } } = useForm({ defaultValues });

    useEffect(() => {

        //화면 첫 입장
        window.scrollTo(0, 0);

        axiosInstance.get('/survey/infoList')
            .then(res => {
                if (!res) return;
                setQuestions(res.data.compList || []);
                setMdlQuestions(res.data.mdlList || []);
                const map = {};
                (res.data.answers || []).forEach(a => {
                    if (!map[a.surveySeq]) map[a.surveySeq] = [];
                    map[a.surveySeq].push(a);
                });
                setAnswerMap(map);
            });
    }, []);

    const handleCheckboxChange = (surveySeq, answerNo) => {
        setAnswers(prev => {
            const current = prev[surveySeq] || [];
            const updated = current.includes(answerNo)
                ? current.filter(v => v !== answerNo)
                : [...current, answerNo];
            return { ...prev, [surveySeq]: updated };
        });
    };

    const handleFinalSubmit = async () => {
        const validForm = await trigger(['compNm', 'managerNm', 'phoneNo', 'email']);
        if (!validForm) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const unansweredSeqs = getAllUnansweredSeqs(questions, answers);
        if (unansweredSeqs.length > 0) {
            setUnansweredSet(new Set(unansweredSeqs));
            const el = document.getElementById(`q-${unansweredSeqs[0]}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        if (equipmentList.length === 0) {
            const el = document.getElementById('mdl-section');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            alert('설비를 1개 이상 추가해 주세요.');
            return;
        }

        setUnansweredSet(new Set());
        const formValues = getValues();
        const responses = [];
        const mdlResponses = [];

        questions.forEach(q => {
            if (q.answerType === 'TEXT') {
                responses.push({ surveySeq: q.surveySeq, answerNo: '0', textAnswer: answers[q.surveySeq] || null });
            } else if (q.answerType === 'RADIO') {
                const selected = answers[q.surveySeq] || '0';
                responses.push({
                    surveySeq: q.surveySeq,
                    answerNo: selected,
                    textAnswer: selected !== '0' ? (extraTexts[`${q.surveySeq}_${selected}`] || null) : null,
                });
            } else if (q.answerType === 'CHECKBOX') {
                const selectedList = answers[q.surveySeq] || [];
                if (selectedList.length > 0) {
                    selectedList.forEach(answerNo => {
                        responses.push({ surveySeq: q.surveySeq, answerNo, textAnswer: extraTexts[`${q.surveySeq}_${answerNo}`] || null });
                    });
                } else {
                    responses.push({ surveySeq: q.surveySeq, answerNo: '0', textAnswer: null });
                }
            }
        });

        equipmentList.forEach((equip, equipIdx) => {
            const mdlNo = equipIdx + 1;
            mdlQuestions.forEach(q => {
                if (q.answerType === 'TEXT') {
                    mdlResponses.push({ mdlNo, mdlNm: equip.mdlNm, surveySeq: q.surveySeq, answerNo: '0', textAnswer: equip.answers[q.surveySeq] || null });
                } else if (q.answerType === 'RADIO') {
                    const selected = equip.answers[q.surveySeq] || '0';
                    mdlResponses.push({
                        mdlNo, mdlNm: equip.mdlNm, surveySeq: q.surveySeq,
                        answerNo: selected,
                        textAnswer: selected !== '0' ? (equip.extraTexts[`${q.surveySeq}_${selected}`] || null) : null,
                    });
                } else if (q.answerType === 'CHECKBOX') {
                    const selectedList = equip.answers[q.surveySeq] || [];
                    if (selectedList.length > 0) {
                        selectedList.forEach(answerNo => {
                            mdlResponses.push({ mdlNo, mdlNm: equip.mdlNm, surveySeq: q.surveySeq, answerNo, textAnswer: equip.extraTexts[`${q.surveySeq}_${answerNo}`] || null });
                        });
                    } else {
                        mdlResponses.push({ mdlNo, mdlNm: equip.mdlNm, surveySeq: q.surveySeq, answerNo: '0', textAnswer: null });
                    }
                }
            });
        });

        axiosInstance.post('/survey/submit', { ...formValues, responses, mdlResponses }, {
            headers: { 'Content-Type': 'application/json' },
        })
            .then(res => {
                if (res.data.resultCode === 'SUCCESS') {
                    setIsSubmitted(true);
                } else {
                    alert(res.data.resultMessage || '제출 중 오류가 발생했습니다.');
                }
            })
            .catch(() => alert('제출 중 오류가 발생했습니다.'));
    };

    const renderAnswer = (q) => {
        const choices = answerMap[q.surveySeq] || [];

        const isNumberOnly = NUMBER_ONLY_SEQS.includes(String(q.surveySeq));

        if (q.answerType === 'TEXT') {
            return (
                <TextField
                    multiline={!isNumberOnly}
                    rows={isNumberOnly ? 1 : 4}
                    fullWidth
                    placeholder={isNumberOnly ? '숫자만 입력해 주세요.' : '답변을 입력해 주세요.'}
                    inputProps={isNumberOnly ? { inputMode: 'numeric' } : {}}
                    value={answers[q.surveySeq] || ''}
                    onChange={(e) => {
                        const val = isNumberOnly ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
                        setAnswers(prev => ({ ...prev, [q.surveySeq]: val }));
                    }}
                />
            );
        }

        if (q.answerType === 'RADIO') {
            return (
                <FormControl component="fieldset">
                    <RadioGroup
                        value={answers[q.surveySeq] || ''}
                        onChange={(e) => setAnswers(prev => ({ ...prev, [q.surveySeq]: e.target.value }))}
                    >
                        {choices.map(ans => (

                            <Box key={ans.answerNo}>
                                <FormControlLabel
                                    value={ans.answerNo}
                                    control={<Radio />}
                                    label={ans.answerContent}
                                />
                                {ans.hasText === 'Y' && answers[q.surveySeq] === ans.answerNo && (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder={isNumberOnly ? '숫자만 입력해 주세요.' : '내용을 입력해 주세요.'}
                                        inputProps={isNumberOnly ? { inputMode: 'numeric' } : {}}
                                        value={extraTexts[`${q.surveySeq}_${ans.answerNo}`] || ''}
                                        onChange={(e) => {
                                            const val = isNumberOnly ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
                                            setExtraTexts(prev => ({ ...prev, [`${q.surveySeq}_${ans.answerNo}`]: val }));
                                        }}
                                        sx={{ mb: 1 }}
                                    />
                                )}
                            </Box>
                        ))}
                    </RadioGroup>
                </FormControl>
            );
        }

        if (q.answerType === 'CHECKBOX') {
            return (
                <FormGroup>
                    {choices.map(ans => (
                        <Box key={ans.answerNo}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={(answers[q.surveySeq] || []).includes(ans.answerNo)}
                                        onChange={() => handleCheckboxChange(q.surveySeq, ans.answerNo)}
                                    />
                                }
                                label={ans.answerContent}
                            />
                            {ans.hasText === 'Y' && (answers[q.surveySeq] || []).includes(ans.answerNo) && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder={isNumberOnly ? '숫자만 입력해 주세요.' : '내용을 입력해 주세요.'}
                                    inputProps={isNumberOnly ? { inputMode: 'numeric' } : {}}
                                    value={extraTexts[`${q.surveySeq}_${ans.answerNo}`] || ''}
                                    onChange={(e) => {
                                        const val = isNumberOnly ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
                                        setExtraTexts(prev => ({ ...prev, [`${q.surveySeq}_${ans.answerNo}`]: val }));
                                    }}
                                    sx={{ mb: 1 }}
                                />
                            )}
                        </Box>
                    ))}
                </FormGroup>
            );
        }
    };

    if (isSubmitted) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '10%' }}>
                <Typography variant="h5" color="#334475" fontWeight="bold" sx={{mb: 2}}>설문에 참여해 주셔서 대단히 감사합니다.</Typography>
                <Typography variant="h6" sx={{mb: 2}}>수집된 정보는 연구 목록 이외에는 사용되지 않으며, 개별 업체 정보는 외부에 공개되지 않습니다.</Typography>
                <Typography variant="h6" >문의사항이 있으시면 아래 담당자에게 연락해 주십시오.</Typography>
                <Box component="table" sx={{ mt: 3, borderCollapse: 'collapse', width: '100%' }}>
                    <Box component="tbody">
                        <Box component="tr">
                            <Box component="td" sx={{ border: '1px solid #ccc', p: 1.5, fontWeight: 'bold', bgcolor: '#f5f5f5', width: '50%' }}>담당 연구원</Box>
                            <Box component="td" sx={{ border: '1px solid #ccc', p: 1.5, width: '50%' }}>임규범 / SQI소프트 담당자</Box>
                        </Box>
                        <Box component="tr">
                            <Box component="td" sx={{ border: '1px solid #ccc', p: 1.5, fontWeight: 'bold', bgcolor: '#f5f5f5' }}>이메일</Box>
                            <Box component="td" sx={{ border: '1px solid #ccc', p: 1.5 }}>newrich01@sqisoft.com</Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 720, mx: 'auto', px: 4, pb: 12 }}>
            {showHint && <ScrollHintOverlay onDismiss={dismissHint} />}

            {/* 헤더 + 업체정보 (첫 화면) */}
            <Box sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}>
                {/* 헤더 */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography fontWeight="bold" sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>
                        한국에너지공단
                    </Typography>
                    <Typography fontWeight="bold" sx={{ fontSize: 18, mb: 0.5, color: '#0029a3' }}>
                        신재생에너지 통합모니터링시스템(REMS)
                    </Typography>
                    <Typography fontWeight="bold" sx={{ fontSize: 22, mb: 1, color: '#3a84a6' }}>
                        원격단말장치(RTU) 현황조사 설문지
                    </Typography>
                    <Box sx={{ borderBottom: '2px solid #E65100', mb: 1.5 }} />
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                        RTU 표준통신규격 및 보안기준 연구 과업
                    </Typography>
                </Box>

                {/* 업체 정보 */}
                <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>업체 정보</Typography>
                <TextField
                    label="업체명 *" size="small" variant="standard" fullWidth sx={{ mb: 1 }}
                    {...register('compNm', { required: '업체명은 필수입니다.' })}
                    error={!!errors.compNm} helperText={errors.compNm?.message}
                />
                <TextField
                    label="담당자명 *" size="small" variant="standard" fullWidth sx={{ mb: 1 }}
                    {...register('managerNm', { required: '담당자명은 필수입니다.' })}
                    error={!!errors.managerNm} helperText={errors.managerNm?.message}
                />
                <TextField
                    label="연락처 *" size="small" variant="standard" fullWidth sx={{ mb: 1 }}
                    {...register('phoneNo', { required: '연락처는 필수입니다.' })}
                    error={!!errors.phoneNo} helperText={errors.phoneNo?.message}
                />
                <TextField
                    label="이메일 *" size="small" variant="standard" fullWidth
                    {...register('email', { required: '이메일은 필수입니다.' })}
                    error={!!errors.email} helperText={errors.email?.message}
                />
                </Box>
            </Box>

            {/* 설문 참여 안내 */}
            <Box sx={{ mb: 4 }}>
                <Typography fontWeight="bold" align="center" sx={{ fontSize: 18, mb: 2 }}>
                    【 설문 참여 안내 】
                </Typography>
                <Box sx={{ border: '1px solid #1565C0', borderRadius: 2, backgroundColor: '#E3F2FD', p: 2, mb: 2 }}>
                    <Typography fontWeight="bold" sx={{ fontSize: 14, mb: 1, color: '#1565C0' }}>조사 목적</Typography>
                    <Typography sx={{ fontSize: 13, lineHeight: 1.8 }}>
                        본 설문은 한국에너지공단의 REMS 연동 RTU 표준통신규격 및 보안기준 수립을 위한
                        기초 현황 자료 수집을 목적으로 합니다. 조사 결과는 RTU 보안등급 체계, 원격 업데이트
                        (FOTA) 기준, SDK 개발 방향 등 정책 수립의 근거로 활용됩니다.
                    </Typography>
                </Box>
                <Box sx={{ border: '1px solid #2E7D32', borderRadius: 2, backgroundColor: '#E8F5E9', p: 2, mb: 2 }}>
                    <Typography fontWeight="bold" sx={{ fontSize: 14, mb: 1, color: '#2E7D32' }}>응답 대상 및 방법</Typography>
                    <Typography sx={{ fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                        {'· 응답 대상: REMS 연동 RTU를 제조하거나 납품·운영하는 업체\n· 제출 방법: 작성 후 이메일 회신 또는 담당자 제출\n· 소요 시간: 약 15~20분\n· 비밀 보장: 응답 내용은 개별 공개 없이 통계적으로만 활용됩니다.'}
                    </Typography>
                </Box>
                <Box sx={{ border: '1px solid #E65100', borderRadius: 2, backgroundColor: '#FFF3E0', p: 2 }}>
                    <Typography fontWeight="bold" sx={{ fontSize: 14, mb: 1, color: '#E65100' }}>⚠ 응답 시 유의사항</Typography>
                    <Typography sx={{ fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                        {'· 모든 답변은 현재 실제 운영 상태 기준으로 작성해 주십시오.\n· 정확히 알 수 없는 항목은 \'정확히 알 수 없음\'을 선택하셔도 무방합니다.\n· 일부 항목은 자유 서술로 추가 기재하실 수 있습니다.'}
                    </Typography>
                </Box>
            </Box>



            {/* 질문 목록 */}
            {questions.map((q, idx) => (
                <React.Fragment key={q.surveySeq}>
                    {SECTION_TITLES[q.ord] && (
                        <Box sx={{ backgroundColor: '#334475', px: 2, py: 1.5, borderRadius: 1, mb: 2 }}>
                            <Typography fontWeight="bold" color="#fff" sx={{ fontSize: 15 }}>{SECTION_TITLES[q.ord]}</Typography>
                        </Box>
                    )}
                    <Box
                        id={`q-${q.surveySeq}`}
                        sx={{
                            mb: 4, p: 1.5, borderRadius: 2,
                            border: unansweredSet.has(q.surveySeq) ? '1.5px solid #d32f2f' : '1px solid #e0e0e0',
                            backgroundColor: unansweredSet.has(q.surveySeq) ? '#fff5f5' : 'background.paper',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {idx + 1} / {questions.length}
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.5, mb: 2 }}>
                            <span style={{ color: '#1a40d5', fontWeight: 700 }}>Q{q.ord}. </span>{q.question}
                        </Typography>
                        {unansweredSet.has(q.surveySeq) && (
                            <Typography sx={{ fontSize: 12, color: '#d32f2f', mb: 1 }}>
                                ※ 필수 응답 항목입니다.
                            </Typography>
                        )}
                        {renderAnswer(q)}
                    </Box>
                </React.Fragment>
            ))}

            {/* 설비 설문 섹션 */}
            <Box id="mdl-section" sx={{ backgroundColor: '#33754a', color: '#fff', px: 2, py: 1.5, borderRadius: 1, mb: 2 }}>
                <Typography fontWeight="bold" color="#fff" sx={{ fontSize: 15 }}>⭐ 제7절 설비별 현황 설문</Typography>
            </Box>
            {equipmentList.map((equip, idx) => (
                <Box key={idx} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontWeight="bold">{equip.mdlNm}</Typography>
                    <Button size="small" color="error" onClick={() => setEquipmentList(prev => prev.filter((_, i) => i !== idx))}>삭제</Button>
                </Box>
            ))}
            <Button
                variant="outlined"
                fullWidth
                height="50px"
                size="large"
                onClick={() => setMdlModalOpen(true)}
                sx={{ mb: 4 }}
            >+ 설비 추가</Button>

            <SurveyMdlModal
                open={mdlModalOpen}
                mdlQuestions={mdlQuestions}
                answerMap={answerMap}
                onClose={() => setMdlModalOpen(false)}
                onSave={(equip) => {
                    setEquipmentList(prev => [...prev, equip]);
                    setMdlModalOpen(false);
                }}
            />

            {/* 제출 버튼 */}
            <Button
                variant="contained"
                color="primary"
                onClick={handleFinalSubmit}
                sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 10 }}
            >제출하기</Button>
        </Box>
    );
}

export default SurveyPage;
